import { MicVAD, type RealTimeVADOptions } from "@ricky0123/vad-web";
import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { useChatStore } from "../state/chatStore";

// VAD (continuous conversation): @ricky0123/vad-web (Silero VAD v5) の薄い wrapper。
// (spec: docs/superpowers/specs/2026-07-07-m3-continuous-conversation-requirements.md R3.2.1, §4.1 Q1)
// 初期パラメータは spec §4.1 の初期設定案。§4.3 Q5 で 5 人試行後にチューニング。
type VadOpts = {
  onSpeechStart: () => void;
  // minSpeechMs 到達時に発火する (spec 2026-07-28 D11)。onSpeechStart は
  // しきい値超えの最初の 1 フレーム (16 kHz / 512 samples = 32ms) で発火するので、
  // 咳や物音でも鳴ってしまう。読み上げを止めるかどうかの判断にはこちらを使う。
  onSpeechRealStart?: () => void;
  onSpeechEnd: (audio: Float32Array) => void;
  onError?: (e: unknown) => void;
  onVADMisfire?: () => void;
  // 上書き用 (dev overlay からのチューニングを想定)
  positiveSpeechThreshold?: number;
  minSpeechMs?: number;
  redemptionMs?: number;
  preSpeechPadMs?: number;
};

// Silero v5 の default (spec §4.1 Q1)
const DEFAULTS = {
  positiveSpeechThreshold: 0.5,
  minSpeechMs: 200,
  redemptionMs: 800,
  preSpeechPadMs: 500,
} as const;

export class VadController {
  private vad: MicVAD | null = null;
  private starting: Promise<void> | null = null;
  // 2026-07-10: start() で MicVAD.new() await 中に destroy() が呼ばれた場合、
  // await 完了後に this.vad = vad で焼き付いて onSpeechStart callback が leak する。
  // destroyed flag を立てておいて await 完了後にチェックし、leak を防ぐ。
  private destroyed = false;

  constructor(private opts: VadOpts) {}

  /**
   * Silero VAD を起動する。既に起動済み (または起動処理中) なら noop で
   * 同じ Promise を返す。
   *
   * MediaStreamManager が保持する session 共有の MediaStream をそのまま
   * 渡す。vad-web の RealTimeVADOptions に `stream` という field は無く、
   * 代わりに `getStream` / `pauseStream` / `resumeStream` という関数群で
   * stream の生死を VAD 自身に管理させる設計になっている。デフォルト実装は
   * `getStream` が独自に `getUserMedia()` を呼び、`pauseStream` は track を
   * `stop()` してしまう。ここで override せずに渡した stream を無視させる
   * ままだと (a) start() のたびに mic 許可ダイアログが二重に出る、
   * (b) pause() で共有 stream の track が停止し MediaStreamManager 側の
   * resume() (`track.enabled = true`) が効かなくなる、という実害が出る。
   * そのため 3 つとも override し、stream lifecycle は MediaStreamManager
   * に一任する (VadController は VAD の解析処理の start/pause/resume だけ
   * を担当する)。
   */
  async start(stream: MediaStream): Promise<void> {
    if (this.vad) return; // idempotent
    if (this.starting) return this.starting;
    // 新しい lifecycle: destroyed flag をリセット (destroy 後の再 start をサポート)
    this.destroyed = false;
    this.starting = (async () => {
      try {
        const options: Partial<RealTimeVADOptions> = {
          model: "v5",
          positiveSpeechThreshold:
            this.opts.positiveSpeechThreshold ?? DEFAULTS.positiveSpeechThreshold,
          minSpeechMs: this.opts.minSpeechMs ?? DEFAULTS.minSpeechMs,
          redemptionMs: this.opts.redemptionMs ?? DEFAULTS.redemptionMs,
          preSpeechPadMs: this.opts.preSpeechPadMs ?? DEFAULTS.preSpeechPadMs,
          // spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §6.2
          // event #9-11 vad.speech_start / vad.speech_end / vad.misfire
          // emit を先に呼び、既存 opts callback は後 (subscriber の throw は eventBus 内部で isolate)。
          // payload.utteranceIndex は Task 2 の SessionEventBuffer.onEvent() 側で採番するため
          // ここでは空 payload で送出する (spec §7.2)。
          onSpeechStart: () => {
            const sessionId = useChatStore.getState().sessionId;
            if (sessionId) {
              eventBus.emit(makeEvent("vad.speech_start", sessionId, {}));
            }
            this.opts.onSpeechStart();
          },
          // 発話が minSpeechMs 続いた時点で 1 回だけ発火する。vad.speech_start は
          // onSpeechStart 側で emit 済みなので、ここでは素通しだけ行う
          // (同じ発話で 2 つイベントが出ると発話数の集計が二重になる)。
          onSpeechRealStart: () => {
            this.opts.onSpeechRealStart?.();
          },
          onSpeechEnd: (audio: Float32Array) => {
            const sessionId = useChatStore.getState().sessionId;
            if (sessionId) {
              // Silero VAD は 16 kHz サンプリング前提 (spec §6.3 VadSpeechEndPayload)。
              eventBus.emit(
                makeEvent("vad.speech_end", sessionId, {
                  samples: audio.length,
                  durationMs: Math.round((audio.length / 16000) * 1000),
                })
              );
            }
            this.opts.onSpeechEnd(audio);
          },
          onVADMisfire: () => {
            const sessionId = useChatStore.getState().sessionId;
            if (sessionId) {
              eventBus.emit(makeEvent("vad.misfire", sessionId, {}));
            }
            this.opts.onVADMisfire?.();
          },
          // 既存の共有 stream をそのまま使わせる (再取得させない)
          getStream: () => Promise.resolve(stream),
          // track を stop/再取得させない。stream の生死は MediaStreamManager が握る。
          pauseStream: () => Promise.resolve(),
          resumeStream: () => Promise.resolve(stream),
          // WASM / ONNX asset の配置場所 (vite.config.ts の viteStaticCopy と一致)
          baseAssetPath: "/vad-assets/",
          onnxWASMBasePath: "/vad-assets/",
        };
        const vad = await MicVAD.new(options);
        // await 中に destroy() が呼ばれた場合、この vad は既に不要 → 破棄して leak を防ぐ
        if (this.destroyed) {
          vad.destroy();
          return;
        }
        vad.start();
        this.vad = vad;
        // MicVAD.new() await 中に setRedemptionMs された場合の値を反映
        vad.setOptions({ redemptionMs: this.opts.redemptionMs ?? DEFAULTS.redemptionMs });
      } catch (e) {
        this.opts.onError?.(e);
        throw e;
      } finally {
        this.starting = null;
      }
    })();
    return this.starting;
  }

  /** VAD の解析処理を一時停止する (destroy はしない。stream には触れない)。 */
  pause(): void {
    this.vad?.pause();
  }

  /**
   * VAD の解析処理を再開する。
   * Silero VAD (vad-web) には明示的な resume API が無いため、initialized
   * 状態の instance に対して start() を呼ぶ (vad-web 内部でこれが resume
   * として扱われる)。
   */
  resume(): void {
    this.vad?.start();
  }

  /**
   * 発話終了とみなす末尾無音 (redemption) を動的に変更する (質問タイプ別の
   * 発話終了判定、spec 2026-07-18 §5.5)。MicVAD.setOptions は同期 merge で
   * ONNX 再ロード・stream 再取得は発生しない。start 前 / MicVAD.new() await 中の
   * 呼び出しにも備えて opts 側にも保持し、start 完了時に最新値を焼き込む。
   */
  setRedemptionMs(ms: number): void {
    this.opts = { ...this.opts, redemptionMs: ms };
    this.vad?.setOptions({ redemptionMs: ms });
  }

  /** VAD instance を破棄する。以後 isRunning() は false を返す。 */
  destroy(): void {
    // start() 中の場合、await 完了時に leak しないよう flag を立てる
    this.destroyed = true;
    this.vad?.destroy();
    this.vad = null;
  }

  isRunning(): boolean {
    return this.vad !== null;
  }
}
